import { useMemo } from 'react'
import { LineSeries } from 'src/components/analytics/charts/LineChart'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { formatNumberToTwoDecimals } from 'src/lib/utils/analytics'

const EMPTY_CHART = {
  series: [],
  conversionRateComparison: {
    withDiscount: 0,
    withoutDiscount: 0
  },
  orderPlaceComparison: {
    withDiscount: 0,
    withoutDiscount: 0
  },
  averageOrderValueComparison: {
    withDiscount: 0,
    withoutDiscount: 0
  }
}

export type UseDiscountOptions = { forKey: 'conversion' | 'average_order_value' | 'completed_count' }

export const useDiscountData = ({ forKey }: UseDiscountOptions) => {
  const {
    getters: { refreshKey, selectedTimeScale }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.discounts_conversion_rate,
      dependencies: {
        endpoints: ['/discount-conversion-timescale']
      }
    },
    refreshKey
  })

  type DiscountDataItem = {
    average_order_value: number
    completed_count: number
    conversion: number
    date: string
    draft_count: number
    total_gmv: number
  }

  const discountData: Omit<LineSeries, 'selectedTimeScale'> & {
    conversionRateComparison: {
      withDiscount: number
      withoutDiscount: number
    }
    orderPlaceComparison: {
      withDiscount: number
      withoutDiscount: number
    }
    averageOrderValueComparison: {
      withDiscount: number
      withoutDiscount: number
    }
  } = useMemo(() => {
    const rawData: {
      NO_DISCOUNT: {
        data: DiscountDataItem[]
        summary: {
          average_order_value: number
          total_conversion: number
          total_orders: number
        }
      }
      DISCOUNT: {
        data: DiscountDataItem[]
        summary: {
          average_order_value: number
          total_conversion: number
          total_orders: number
        }
      }
    } = data[0]?.data?.result?.widgetData?.data

    if (!rawData) {
      return EMPTY_CHART
    }

    const nonDiscountData = rawData.NO_DISCOUNT?.data
    const discountData = rawData.DISCOUNT?.data

    const nonDiscountDataSummary = rawData.NO_DISCOUNT?.summary
    const discountDataSummary = rawData.DISCOUNT?.summary
    const calculatedData = {
      series: [
        {
          data: nonDiscountData?.map(item => {
            return {
              x: item.date,
              y: formatNumberToTwoDecimals(item[forKey])
            }
          }),
          label: 'No Discount',
          color: '#8541A4'
        },
        {
          data: discountData?.map(item => {
            return {
              x: item.date,
              y: formatNumberToTwoDecimals(item[forKey])
            }
          }),
          label: 'Discount',
          color: '#01cca7'
        }
      ],
      conversionRateComparison: {
        withDiscount: discountDataSummary?.total_conversion ?? 0,
        withoutDiscount: nonDiscountDataSummary?.total_conversion ?? 0
      },
      orderPlaceComparison: {
        withDiscount: discountDataSummary?.total_orders ?? 0,
        withoutDiscount: nonDiscountDataSummary?.total_orders ?? 0
      },
      averageOrderValueComparison: {
        withDiscount: discountDataSummary?.average_order_value ?? 0,
        withoutDiscount: nonDiscountDataSummary?.average_order_value ?? 0
      }
    }

    calculatedData.series = calculatedData.series.filter(series => series.data?.length > 0)
    return calculatedData
  }, [data, forKey])

  return {
    ref,
    isLoading,
    isError,
    discountData,
    selectedTimeScale
  }
}
